import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

/* ---------- animations ---------- */

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const gradientShift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50%      { background-position: 100% 50%; }
`;

/* ---------- styled ---------- */

const HeroContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  animation: ${fadeInUp} 0.5s ease;
`;

const Headline = styled.h1`
  font-size: 2.75rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.15;
  margin: 0 0 1.25rem;
  background: linear-gradient(
    135deg,
    var(--accent) 0%,
    #3b82f6 50%,
    var(--accent) 100%
  );
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${gradientShift} 6s ease-in-out infinite;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Description = styled.p`
  font-size: 1.0625rem;
  color: var(--text-secondary);
  max-width: 560px;
  line-height: 1.7;
  margin: 0 0 2.5rem;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  max-width: 640px;
  margin-bottom: 2.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Chip = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.125rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  font-size: 0.875rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.25s;
  font-family: inherit;

  &:hover {
    border-color: var(--accent);
    background: var(--accent-subtle);
    color: var(--accent);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

const ChipIcon = styled.span`
  font-size: 1.125rem;
  flex-shrink: 0;
`;

const PromptArea = styled.div`
  width: 100%;
  max-width: 560px;
`;

const PromptInput = styled.input`
  width: 100%;
  padding: 1rem 1.25rem;
  background: var(--cmd-bg);
  border: 2px solid var(--border);
  border-radius: 14px;
  font-family: inherit;
  font-size: 1rem;
  color: var(--text-primary);
  transition: all 0.3s;
  outline: none;
  box-shadow: var(--shadow-md);

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 4px var(--ring), var(--shadow-lg);
  }
`;

const PromptMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.625rem;
`;

const ModelPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-subtle);
  }
`;

const ModelPillIcon = styled.span`
  font-size: 0.75rem;
`;

const ModelPillChevron = styled.span`
  font-size: 0.5rem;
  color: var(--text-muted);
`;

const PromptHint = styled.span`
  font-size: 0.6875rem;
  color: var(--text-muted);
`;

/* ---------- constants ---------- */

const SUGGESTED_QUERIES = [
  {
    icon: '\u{1F4CA}',
    label: 'I need to create some slides',
    prompt:
      'I need to create some slides for my quarterly business review',
  },
  {
    icon: '\u{1F50D}',
    label: 'Help me understand business behaviour',
    prompt:
      'I need help to understand some specific behaviour in my business',
  },
  {
    icon: '\u{1F4E7}',
    label: 'Create email reminders',
    prompt:
      'I need to create some email reminders for my business state',
  },
  {
    icon: '\u{1F4C1}',
    label: 'Analyse this spreadsheet',
    prompt:
      'I need to analyse this spreadsheet and find key insights',
  },
];

/* ---------- component ---------- */

interface EmptyStateProps {
  onSend: (query: string) => void;
  modeSelector?: React.ReactNode;
}

const EmptyState: React.FC<EmptyStateProps> = ({ onSend, modeSelector }) => {
  const [input, setInput] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const text = input.trim();
      if (!text) return;
      setInput('');
      onSend(text);
    }
  };

  return (
    <HeroContainer>
      <Headline>Explore. Analyze. Build.</Headline>

      <Description>
        Lumina Factory connects your systems, explores possibilities with
        intelligent agents, helps you make key decisions, and builds powerful
        artefacts you can reuse across your work and life.
      </Description>

      <ChipsRow>
        {SUGGESTED_QUERIES.map((q) => (
          <Chip key={q.label} type="button" onClick={() => onSend(q.prompt)}>
            <ChipIcon>{q.icon}</ChipIcon>
            {q.label}
          </Chip>
        ))}
      </ChipsRow>

      <PromptArea>
        <PromptInput
          type="text"
          placeholder="Describe what you need..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <PromptMeta>
          <ModelPill type="button" title="Change AI model">
            <ModelPillIcon>{'\u2728'}</ModelPillIcon>
            <span>Claude 4 Opus</span>
            <ModelPillChevron>{'\u25BC'}</ModelPillChevron>
          </ModelPill>
          {modeSelector}
          <PromptHint>{'\u21A9'} to start</PromptHint>
        </PromptMeta>
      </PromptArea>
    </HeroContainer>
  );
};

export default EmptyState;
